import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import classNames from 'classnames';
import { designSystem, Modal, Panel, PanelGroup } from '@yola/ws-ui';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import SearchBar from './search-bar';
import ElementTypeSettings from './element-type-settings';
import constants from '../constants';
import FontPreviewOption from './font-preview-option';
import EmptyState from './empty-state';

const {
  DialogHeader,
  Box,
  Stack,
  DialogButtonGroup,
  DialogButton,
  Paragraph,
  Heading,
  DialogButtonGroupSeparator,
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionContent,
} = designSystem;

const { DEFAULT_DIALOG_HEIGHT, DEFAULT_DIALOG_WIDTH, DEFAULT_DIALOG_MIN_HEIGHT } = constants;

const ElementTypeOption = ({ children, observer }) => (
  <FontPreviewOption observer={observer}>{children}</FontPreviewOption>
);

const FontsSettingsDialog = ({
  offsetX,
  offsetY,
  captions,
  control,
  fontObserver,
  contentRef,
  activeAccordionItems,
  accordionSections,
  isAdvancedFontCustomizationAvailable,
  basicLineHeightValues,
  onToggleAccordionItem,
  onCancel,
  onSubmit,
  onDragEnd,
  handleFeatureAvailability,
  handlers,
  onManageColorsClick,
  handleSearchQueryChange,
  handleSearchQueryClear,
  handleSearchFocus,
}) => {
  const modalContentRef = useRef(null);

  const searchBarPanelClasses = classNames(
    'ws-design-fonts-dialog__flex-shrink-no',
    'ws-design-fonts-dialog__border-bottom-no',
    'ws-design-fonts-dialog__search-bar'
  );

  const contentPanelClasses = classNames(
    'ws-design-fonts-dialog__content-wrapper',
    'ws-design-fonts-dialog__border-bottom-no'
  );

  const contentClasses = classNames('ws-design-fonts-dialog__content', {
    'ws-design-fonts-dialog__empty-state': !accordionSections.length,
  });

  return (
    <Modal
      modalContentRef={modalContentRef}
      draggable
      fullscreen={bowser.mobile}
      width={DEFAULT_DIALOG_WIDTH}
      minWidth={DEFAULT_DIALOG_WIDTH}
      maxWidth={DEFAULT_DIALOG_WIDTH}
      height={DEFAULT_DIALOG_HEIGHT}
      minHeight={DEFAULT_DIALOG_MIN_HEIGHT}
      offsetX={offsetX}
      offsetY={offsetY}
      dragHandleSelector=".ws-drag-trigger"
      dragCancelSelector=".ws-ui-dialog-header-controls"
      overlay="none"
      // we have to prevent submiting dialog on overlay click if upgrade dialog is showed
      floatingModalElementSelector=".wp-dialog-overlay, .product-select-container, .ws-ui-base-select-option, .ws-ui-base-select__option"
      handleCancel={onCancel}
      handleSubmit={onSubmit}
      onOverlayClick={onSubmit}
      onDragEnd={onDragEnd}
      className="ws-design-fonts-dialog"
      resizeIcon="ns-resize"
    >
      <PanelGroup height="100%">
        <Panel
          align="middle"
          corners="squared"
          className="ws-drag-trigger ws-design-fonts-dialog__flex-shrink-no"
        >
          <DialogHeader paddingBottom="spacing-s">
            <Box>
              <Box paddingBottom="spacing-2-xs">
                <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
              </Box>
              <Box>
                <Stack>
                  <Heading type="heading-3">{captions.title}</Heading>
                  <Paragraph>{captions.description}</Paragraph>
                </Stack>
              </Box>
            </Box>
          </DialogHeader>
        </Panel>
        <Panel className={searchBarPanelClasses}>
          <SearchBar
            sticky
            {...(accordionSections.length && { borderless: true })}
            placeholder={captions.searchPlaceholder}
            onChange={handleSearchQueryChange}
            onClear={handleSearchQueryClear}
            onFocus={handleSearchFocus}
          />
        </Panel>
        <Panel height="100%" scrollable rootRef={contentRef} className={contentPanelClasses}>
          <div className={contentClasses}>
            {accordionSections.length ? (
              <Accordion
                appearance="grey"
                defaultActiveItem={activeAccordionItems}
                onToggleItem={onToggleAccordionItem}
              >
                {accordionSections.map(({ id: sectionId, title: sectionTitle }) => (
                  <AccordionGroup key={sectionId} itemId={sectionId}>
                    <AccordionHeader
                      title={sectionTitle.toUpperCase()}
                      isSticky
                      {...(!isAdvancedFontCustomizationAvailable && { tagProps: { label: 'PRO' } })}
                    />
                    <AccordionContent>
                      <ElementTypeSettings
                        control={control}
                        id={sectionId}
                        captions={captions}
                        fontObserver={fontObserver}
                        modalContentRef={modalContentRef}
                        contentRef={contentRef}
                        basicLineHeightValues={basicLineHeightValues}
                        isAdvancedFontCustomizationAvailable={isAdvancedFontCustomizationAvailable}
                        handlers={handlers}
                        onManageColorsClick={onManageColorsClick}
                        handleFeatureAvailability={handleFeatureAvailability}
                      />
                    </AccordionContent>
                  </AccordionGroup>
                ))}
              </Accordion>
            ) : (
              <EmptyState title={captions.searchEmptyTitle} subtitle={captions.searchEmptyText} />
            )}
          </div>
        </Panel>
        <Panel corners="squared" className="ws-design-fonts-dialog__flex-shrink-no">
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

ElementTypeOption.propTypes = {
  children: PropTypes.node.isRequired,
  observer: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
};

FontsSettingsDialog.propTypes = {
  offsetX: PropTypes.number.isRequired,
  offsetY: PropTypes.number.isRequired,
  captions: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    description: PropTypes.string,
    submitButton: PropTypes.string,
    cancelButton: PropTypes.string,
    elements: PropTypes.shape({}),
    settings: PropTypes.shape({}),
    searchPlaceholder: PropTypes.string,
    searchEmptyTitle: PropTypes.string,
    searchEmptyText: PropTypes.string,
    manageColorsButton: PropTypes.string,
    fontSizeNotification: PropTypes.string,
    noResultsText: PropTypes.string,
  }).isRequired,
  control: PropTypes.shape({}).isRequired,
  fontObserver: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
  contentRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  activeAccordionItems: PropTypes.array.isRequired,
  accordionSections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    })
  ).isRequired,
  isAdvancedFontCustomizationAvailable: PropTypes.bool.isRequired,
  basicLineHeightValues: PropTypes.shape({}).isRequired,
  onToggleAccordionItem: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  handleFeatureAvailability: PropTypes.func.isRequired,
  handlers: PropTypes.shape({}).isRequired,
  onManageColorsClick: PropTypes.func.isRequired,
  handleSearchQueryChange: PropTypes.func.isRequired,
  handleSearchQueryClear: PropTypes.func.isRequired,
  handleSearchFocus: PropTypes.func.isRequired,
};

export default FontsSettingsDialog;
